/**
 * RTWO Asset & Token Engine — Transfer Rule Service
 */

import type { AssetId, RuleId, RTWOAssetTransferRule } from '../domain-models';
import type { ITransferRuleService, TransferRuleResult } from '../service-interfaces';
import type { ASSRepositories } from '../repositories/ass-repositories';
import type {
  LedgerAdapterRegistry,
  ScrtRuntimeAdapterRegistry,
} from '../registry/adapter-registries';
import { ASSTInternalEventTypes } from '../events/ass-event-bus';
import { BaseASSService, type ASSServiceContext } from './base-ass-service';

export class TransferRuleService extends BaseASSService implements ITransferRuleService {
  readonly serviceId = 'ass-transfer-rule-service-001';
  readonly serviceName = 'Transfer Rule Service';
  readonly description =
    'Transfer rule service — placeholder, requires real Transfer Rule Backend. No fake transfers. No fake transfer rules.';
  readonly integrations = [
    'Core Engine',
    'Ledger Engine',
    'Smart Contract Runtime',
    'Cryptography Engine',
  ];
  readonly requiresLedgerConnection = true;
  readonly requiresTokenRuntime = true;

  constructor(
    context: ASSServiceContext,
    private readonly repositories: ASSRepositories,
    private readonly ledgerRegistry: LedgerAdapterRegistry,
    private readonly scrtRegistry: ScrtRuntimeAdapterRegistry
  ) {
    super(context);
  }

  async initialize(): Promise<void> {
    this.status = 'waiting_for_ledger_connection';
  }

  async configureTransferRule(
    assetId: AssetId,
    ruleType: string,
    conditionReference: string
  ): Promise<TransferRuleResult> {
    if (!this.ledgerRegistry.isConfigured() || !this.scrtRegistry.isConfigured()) {
      return {
        success: false,
        ruleId: null,
        error: 'Transfer rule configuration requires real Transfer Rule Backend — Not Configured',
        isPlaceholder: true,
        source: 'Asset & Token Engine — Transfer Rule Backend Not Configured',
      };
    }

    const adapterResult = await this.scrtRegistry
      .getActiveAdapter()
      .configureTransferRule(assetId, ruleType, conditionReference);

    if (adapterResult?.rule) {
      this.repositories.transferRules.save(adapterResult.rule);
      this.context.eventBus.emit(ASSTInternalEventTypes.TRANSFER_RULE_CONFIGURED, {
        ruleId: adapterResult.rule.ruleId,
        assetId,
      });
    }

    return {
      success: false,
      ruleId: null,
      error: 'Transfer rule configuration requires real Transfer Rule Backend — Not Configured',
      isPlaceholder: true,
      source: 'Asset & Token Engine — Transfer Rule Backend Not Configured',
    };
  }

  getAllRules(): RTWOAssetTransferRule[] {
    if (!this.scrtRegistry.isConfigured()) return [];
    return this.repositories.transferRules.findAll();
  }

  getRulesByAssetId(assetId: AssetId): RTWOAssetTransferRule[] {
    if (!this.scrtRegistry.isConfigured()) return [];
    return this.repositories.transferRules.findByAssetId(assetId);
  }

  getRule(ruleId: RuleId): RTWOAssetTransferRule | null {
    if (!this.scrtRegistry.isConfigured()) return null;
    return this.repositories.transferRules.findById(ruleId);
  }

  getActiveRuleCount(): 0 {
    return 0;
  }
}
